import { Dispatch, FunctionComponent, SetStateAction, useEffect, useState } from 'react';
import VerticalContainer from '../../common/VerticalContainer';
import PageContainer from '../tableComponents/PageContainer';
import { Box, Button, Typography } from '@mui/material';
import AE from '../../../static/product_image/AE.png';
import Digikey from '../../../static/product_image/Digikey.png';
import Mouser from '../../../static/product_image/Mouser.png';
import TTI from '../../../static/product_image/TTI.png';
import { formatToKPlus } from '../../../utils/FormatInstock';
import { useDistributors } from '../../../graphql/pimcore/modules/contacts/contexts/DistributorsProvider';
import { PIMCORE_CONTENT_BASE_URL } from '../../../utils/Pimcore';
import { CS_BASE_URL } from '../../../utils/ProjectUrls';
import { useResponsive } from '../../../contexts/ResponsiveContext';
import { Alias } from '../../../schemas/Alias';
import { ProductsContent } from '../../../graphql/pimcore/modules/siteContent/types/ProductsContent';
import { toUrl } from '@/utils/convertImagePath';

interface DistributorsProps {
    partNumber: string;
    superAlias?: string | null;
    aliases: Alias[];
    setTotalQuantity: Dispatch<SetStateAction<number>>;
    isKsimSupport: boolean;
    totalQuantity: number;
    contentTree: ProductsContent;
}

interface Distributor {
    logo: string;
    name: string;
    stock: string;
    buyNowUrl: string;
    region: string;
    country: string;
    // 若 API 後續補上國家資訊，可加入 country 欄位
}

interface UserRegion {
    countryCode: string;
    region: string;
}

interface Price {
    price: number;
    quantity: number;
}

interface Prices {
    currency: string;
    prices: Price[];
}

export interface OctoPartResponse {
    octoParts: OctoPart[];
    userRegion: UserRegion;
}

export interface OctoPart {
    seller: string;
    prices: Prices;
    homepageUrl: string;
    region: string;
    country: string;
    quantity: number;
    minOrderQty: number;
    packaging: string | null;
    productUrl: string;
}

const getLogoForSeller = (seller: string) => {
    switch (seller) {
        case 'TTI':
        case 'TTI Europe':
            return TTI;
        case 'Mouser':
            return Mouser;
        case 'DigiKey':
            return Digikey;
        case 'Arrow Electronics':
            return AE;
        default:
            return '';
    }
};

const renameDistributor = (seller: string) => {
    switch (seller) {
        case 'TTI Europe':
            return 'TTI'.toLowerCase();
        case 'Mouser':
            return 'Mouser Electronics'.toLowerCase();
        case 'RS (Formerly Allied Electronics)':
            return 'RS'.toLowerCase();
        case 'Arrow Electronics':
            return 'Arrow'.toLowerCase();
        case 'Chip One Stop':
            return 'Chip 1 Stop'.toLowerCase();
        case 'element14 APAC':
            return 'element14'.toLowerCase();
        default:
            return seller.toLowerCase();
    }
};

const Distributors: FunctionComponent<DistributorsProps> = ({
    partNumber,
    superAlias,
    aliases,
    setTotalQuantity,
    isKsimSupport,
    totalQuantity,
    contentTree,
}) => {
    const [distributors, setDistributors] = useState<Distributor[]>([]);

    const distributorsFromPimCore = useDistributors();

    const { isScreenMd, isScreenSm, isScreenXs, responsiveWidth } = useResponsive();

    const getLogoPathFromPimCore = (distributorName: string) => {
        const renamed = renameDistributor(distributorName);

        const pimCoreDistributor = distributorsFromPimCore.distributors?.find(
            (d) => d.name.toLowerCase() === renamed
        );

        if (!pimCoreDistributor) {
            console.warn(
                'distributorName.toLowerCase() =',
                distributorName.toLowerCase(),
                'renameDistributor =',
                renamed,
                'All PimCore.distributors =',
                distributorsFromPimCore.distributors,
                'All PimCore.distributor names =',
                distributorsFromPimCore.distributors?.map((d) => d.name.toLowerCase())
            );
            return '';
        }

        if (pimCoreDistributor.logo?.fullpath) {
            return `${PIMCORE_CONTENT_BASE_URL}/${pimCoreDistributor.logo.fullpath}`;
        }

        return '';
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                // 取得零件資料
                const requestBody = {
                    uniquePn: partNumber,
                    superAlias: superAlias,
                    aliases: aliases,
                };
                const partsResponse = await fetch(`${CS_BASE_URL}/octopart/parts`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(requestBody),
                });
                const partsData: OctoPartResponse = await partsResponse.json();

                // 將零件資料轉換為 Distributor 資料
                const distributorsFromApi = partsData.octoParts.map((part) => ({
                    logo: toUrl(getLogoForSeller(part.seller)),
                    name: part.seller,
                    stock: `${formatToKPlus(part.quantity)} ${contentTree.filters.checkboxLabels.inStock.content}`,
                    buyNowUrl: part.productUrl,
                    region: part.region,
                    country: part.country,
                }));

                const quantity = partsData.octoParts.reduce((acc, part) => acc + part.quantity, 0);
                setTotalQuantity(quantity);
                setDistributors(distributorsFromApi);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, [partNumber]);

    const smallScreen = isScreenSm || isScreenXs;

    const distributorList = distributors.map((distributor) => {
        if (smallScreen) {
            // smallScreen 版：左右兩個區塊分別垂直排列
            return (
                <Box
                    key={distributor.name}
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        width: '100%',
                        borderBottom: '1px solid var(--Support-Colors-Text, #191A1B)',
                    }}
                >
                    {/* 左邊：如果是 isScreenXs，不顯示 image，只顯示靠左且垂直置中的 distributor.name；否則維持原本的 image + 名稱排版 */}
                    {isScreenXs ? (
                        <Box
                            className="myboxname"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                overflowWrap: 'anywhere',
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    fontFamily: 'Poppins',
                                    fontWeight: 400,
                                    fontSize: '18px',
                                    lineHeight: '116.8%',
                                    color: '#191A1B',
                                    textAlign: 'left',
                                }}
                            >
                                {distributor.name}
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                            }}
                        >
                            {getLogoPathFromPimCore(distributor.name) !== '' && (
                                <img
                                    src={getLogoPathFromPimCore(distributor.name)}
                                    alt={distributor.name}
                                    style={{
                                        objectFit: 'contain',
                                        height: '90px',
                                        backgroundBlendMode: 'multiply',
                                        maxWidth: '300px',
                                    }}
                                />
                            )}

                            <Typography
                                variant="h6"
                                sx={{
                                    fontFamily: 'Poppins',
                                    fontWeight: 400,
                                    fontSize: '18px',
                                    lineHeight: '116.8%',
                                    color: '#191A1B',
                                    marginTop: '8px',
                                    textAlign: 'left',
                                }}
                            >
                                {distributor.name}
                            </Typography>
                        </Box>
                    )}

                    {/* 右邊：Stock 在上，Buy Now 在下 */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            justifyContent: 'space-between',
                            height: '100%',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                justifyContent: 'flex-end', // 2) 將文字對齊右側
                                whiteSpace: 'nowrap', // 3) 防止文字換行
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: isScreenSm ? '18px' : '14px',
                                    color: '#0E8A13',
                                    fontWeight: 'bold',
                                }}
                            >
                                ✔
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: 'Poppins',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: isScreenSm ? '18px' : '14px',
                                    lineHeight: '23px',
                                    color: '#0E8A13',
                                    textAlign: 'right', // 若想讓文字本身對齊右側，也可加上 textAlign
                                }}
                            >
                                {distributor.stock}
                            </Typography>
                        </Box>
                        <Box sx={{ marginTop: '8px' }}>
                            <Button
                                href={distributor.buyNowUrl}
                                target="_blank"
                                variant="contained"
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    padding: '10px 20px',
                                    gap: '10px',
                                    backgroundColor: '#B75332',
                                    borderRadius: '5px',
                                    textTransform: 'none',
                                    whiteSpace: 'nowrap',
                                    transition: 'none',
                                    '&:hover': {
                                        background:
                                            'linear-gradient(0deg, #B75332 0%, #E58869 100%)',
                                        transition: 'none',
                                    },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: 'Poppins',
                                        fontStyle: 'normal',
                                        fontWeight: 400,
                                        fontSize: isScreenSm ? '18px' : '14px',
                                        lineHeight: '19px',
                                        color: '#FFFFFF',
                                    }}
                                >
                                    {contentTree.product.buyNow.content} →
                                </Typography>
                            </Button>
                        </Box>
                    </Box>
                </Box>
            );
        } else if (isScreenMd) {
            return (
                <Box
                    key={distributor.name}
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px',
                        width: '100%',
                        marginBottom: '16px',
                        borderBottom: '1px solid var(--Support-Colors-Text, #191A1B)',
                    }}
                >
                    {/* 左邊：圖片在上、名稱在下 */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                        }}
                    >
                        {getLogoPathFromPimCore(distributor.name) !== '' && (
                            <img
                                src={getLogoPathFromPimCore(distributor.name)}
                                alt={distributor.name}
                                style={{
                                    objectFit: 'contain',
                                    height: '90px',
                                    backgroundBlendMode: 'multiply',
                                    maxWidth: '300px',
                                }}
                            />
                        )}
                        <Typography
                            variant="h6"
                            sx={{
                                fontFamily: 'Poppins',
                                fontWeight: 400,
                                fontSize: '18px',
                                lineHeight: '116.8%',
                                color: '#191A1B',
                                marginTop: '8px',
                            }}
                        >
                            {distributor.name}
                        </Typography>
                    </Box>

                    {/* 右邊：庫存與 BUY NOW 以水平排列 */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',

                            gap: '16px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '16px',
                                    color: '#0E8A13',
                                    fontWeight: 'bold',
                                }}
                            >
                                ✔
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: 'Poppins',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: '18px',
                                    lineHeight: '23px',
                                    color: '#0E8A13',
                                }}
                            >
                                {distributor.stock}
                            </Typography>
                        </Box>
                        <Button
                            href={distributor.buyNowUrl}
                            target="_blank"
                            variant="contained"
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                padding: '8px 16px',
                                gap: '10px',
                                backgroundColor: '#B75332',
                                borderRadius: '5px',
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                                transition: 'none',
                                '&:hover': {
                                    background: 'linear-gradient(0deg, #B75332 0%, #E58869 100%)',
                                    transition: 'none',
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: 'Poppins',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: '16px',
                                    lineHeight: '19px',
                                    color: '#FFFFFF',
                                }}
                            >
                                {contentTree.product.buyNow.content} →
                            </Typography>
                        </Button>
                    </Box>
                </Box>
            );
        } else {
            // 大螢幕版：原有的水平排列布局
            return (
                <Box
                    key={distributor.name}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px',
                        width: '100%',
                        marginBottom: '16px',
                        borderBottom: '1px solid var(--Support-Colors-Text, #191A1B)',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            flexDirection: 'row',
                            alignItems: 'center',
                            padding: '0px',
                            paddingRight: '0.5rem',
                            width: '501px',
                            height: '112.22px',
                            flex: 'none',
                            order: 0,
                            flexGrow: 0,
                        }}
                        data-testid={`box_logo_img_${distributor.name}`}
                    >
                        {getLogoPathFromPimCore(distributor.name) !== '' && (
                            <img
                                src={getLogoPathFromPimCore(distributor.name)}
                                alt={distributor.name}
                                style={{
                                    objectFit: 'contain',
                                    height: '90px',
                                    backgroundBlendMode: 'multiply',
                                    maxWidth: '300px',
                                }}
                            />
                        )}
                        <Typography
                            variant="h6"
                            sx={{
                                width: '12rem',
                                height: '23px',
                                fontFamily: 'Poppins',
                                fontStyle: 'normal',
                                fontWeight: 400,
                                fontSize: '20px',
                                lineHeight: '116.8%',
                                color: '#191A1B',
                                flex: 'none',
                                order: 1,
                                flexGrow: 0,
                            }}
                        >
                            {distributor.name}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            justifyContent: 'space-between',
                            padding: '0px',
                            flexWrap: 'nowrap',
                            flex: '1',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                padding: '0px',
                                gap: '6px',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '16px',
                                    color: '#0E8A13',
                                    fontWeight: 'bold',
                                }}
                            >
                                ✔
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: 'Poppins',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: '20px',
                                    lineHeight: '23px',
                                    color: '#0E8A13',
                                }}
                            >
                                {distributor.stock}
                            </Typography>
                        </Box>
                        <Button
                            href={distributor.buyNowUrl}
                            target="_blank"
                            variant="contained"
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                padding: '10px 20px',
                                gap: '10px',
                                backgroundColor: '#B75332',
                                borderRadius: '5px',
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                                transition: 'none',
                                '&:hover': {
                                    background: 'linear-gradient(0deg, #B75332 0%, #E58869 100%)',
                                    transition: 'none',
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: 'Poppins',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: '16px',
                                    lineHeight: '19px',
                                    color: '#FFFFFF',
                                }}
                            >
                                {contentTree.product.buyNow.content} →
                            </Typography>
                        </Button>
                    </Box>
                </Box>
            );
        }
    });

    return (
        <PageContainer
            sx={{ backgroundColor: isKsimSupport ? 'white' : '#F2F2F2' }}
            containerName={'product-distributor'}
            innerSx={{ width: responsiveWidth }}
        >
            <VerticalContainer sx={{ alignItems: 'flex-start', gap: '24px', width: '100%' }}>
                {/* Title */}
                <Typography
                    sx={{
                        fontFamily: 'Poppins',
                        fontWeight: 700,
                        fontSize: '24px',
                        lineHeight: '100%',
                        marginBottom: '16px',
                    }}
                >
                    {contentTree.product.whereToBuy.content}
                </Typography>

                {/* 若無庫存則顯示提示 */}
                {totalQuantity === 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            marginBottom: '16px',
                        }}
                    >
                        {contentTree.product.noInventoryAvailable.content}
                    </Box>
                )}

                {/* 顯示 Distributor 列表（依據選取的 Region 與 Country 過濾） */}
                {distributorList}
            </VerticalContainer>
        </PageContainer>
    );
};

export default Distributors;
